<!--
// app/components/Shared/slider-matches/SliderMatchesTurn.vue
-->
<script setup lang="ts">
import SliderMatchesTurnMatch from "~/app/components/Shared/slider-matches/SliderMatchesTurnMatch.vue";
import type { Match, MatchCenterData } from "~/src/match/domain/models/Match";
import type { Team } from "~/src/team/domain/models/Team";
import { useRegions } from "~/app/composables/regions";
import RegionUiService from "~/src/region/infrastructure/ui-services/regionUiService";
import { useMatches } from "~/app/composables/season/matches";

const { locale } = useI18n();

type Props = {
  turn: MatchCenterData;
  teams: Record<number, Team>;
  regionReference?: number;
};

const props = defineProps<Props>();
const { regionTimeZone } = useRegions();
const { findRegionInfoByRegionId } = RegionUiService();
const { setMatchTimeZone } = useMatches();

const showGroup = (matches: Match[]): boolean => {
  const groupArr = matches.map(m => m.groupId);
  return [...new Set(groupArr)].length > 1;
};

const regionTimeUsed = props.regionReference
  ? findRegionInfoByRegionId(props.regionReference, "timeZone")
  : regionTimeZone;

const forcedTimezone: boolean =
  !!props.turn.matches[0].metaInformation && !!props.turn.matches[0].metaInformation?.match_timezone;

const computedTimeZone: string =
  setMatchTimeZone(
    forcedTimezone ? "appendix" : "stadium",
    forcedTimezone ? props.turn.matches[0].metaInformation?.match_timezone : props.turn.matches[0].stadiumId
  )?.timezone || regionTimeUsed;

const start = new Date(props.turn.startDate);
const finish = new Date(props.turn.finishDate);

// Get date components in the correct timezone
const startDay = parseInt(start.toLocaleString('en-US', { timeZone: computedTimeZone, day: 'numeric' }));
const startMonth = parseInt(start.toLocaleString('en-US', { timeZone: computedTimeZone, month: 'numeric' }));
const startYear = parseInt(start.toLocaleString('en-US', { timeZone: computedTimeZone, year: 'numeric' }));

const finishDay = parseInt(finish.toLocaleString('en-US', { timeZone: computedTimeZone, day: 'numeric' }));
const finishMonth = parseInt(finish.toLocaleString('en-US', { timeZone: computedTimeZone, month: 'numeric' }));
const finishYear = parseInt(finish.toLocaleString('en-US', { timeZone: computedTimeZone, year: 'numeric' }));

const isSameDate = startDay === finishDay && startMonth === finishMonth && startYear === finishYear;
const isSameMonth = startMonth === finishMonth && startYear === finishYear;
</script>

<template>
  <div class="turn-wrapper">
    <div class="turn-info-wrapper">
      <p class="turn-date">
        <span v-if="isSameDate">
          <nuxt-time
            :datetime="props.turn.startDate"
            :locale="locale"
            :time-zone="computedTimeZone"
            weekday="long"
            month="long"
            day="numeric"
          />
        </span>

        <!-- different turn dates -->
        <span v-else>
          <!-- first day -->
          <NuxtTime
            :datetime="props.turn.startDate"
            :locale="locale"
            :time-zone="computedTimeZone"
            weekday="long"
            :month="!isSameMonth ? 'long' : undefined"
            day="numeric"
          />
          -
          <!-- Lat day -->
          <span class="first-letter:uppercase inline-block">
            <NuxtTime
              :datetime="props.turn.finishDate"
              :locale="locale"
              :time-zone="computedTimeZone"
              weekday="long"
              month="long"
              day="numeric"
            />
          </span>
        </span>
      </p>
      <div class="turn-name">{{ turn.turnName }}</div>
    </div>
    <div class="mt-2 flex gap-1">
      <slider-matches-turn-match
        v-for="match in turn.matches"
        :key="match.id"
        :match="match"
        :teams="teams"
        :show-group="Array.isArray(turn.matches) && showGroup(turn.matches)"
        :region-reference="regionReference"
      />
    </div>
  </div>
</template>

<style scoped>
.turn-wrapper {
  @apply py-3.5 px-5 border-r-2 border-gray-800 min-w-max max-w-max relative;
}

.turn-info-wrapper {
  @apply flex gap-2 items-center justify-start sticky left-6 top-0 pr-4 max-w-max;
}

.turn-date {
  @apply text-gray-300 text-sm font-medium first-letter:uppercase;
}

.turn-name {
  @apply border px-1.5 rounded border-gray-500 justify-center items-center text-center text-gray-500 text-[10px] font-normal leading-[15px];
}
</style>
